import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, Phone, Mail, MapPin, FileText, Heart, FileCheck, RefreshCw, CheckCircle, Filter, Lock, Eye, EyeOff, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin password - change this to your preferred password
const ADMIN_PASSWORD = 'HopeN2026!';

export default function BookingJournal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [cprBookings, setCprBookings] = useState([]);
  const [notaryBookings, setNotaryBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Check if already logged in (session storage)
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('bookingJournalAuth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('bookingJournalAuth', 'true');
      setLoginError('');
    } else {
      setLoginError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('bookingJournalAuth');
  };

  // Export functions
  const exportToCSV = (data, filename, headers) => {
    const csvRows = [];
    csvRows.push(headers.join(','));
    
    data.forEach(item => {
      const values = headers.map(header => {
        const val = item[header] || '';
        // Escape quotes and wrap in quotes if contains comma
        const escaped = String(val).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`Exported ${data.length} records to ${filename}.csv`);
  };

  const exportCPRBookings = () => {
    const headers = ['name', 'email', 'phone', 'courseType', 'preferredDate', 'preferredTime', 'numberOfStudents', 'locationType', 'address', 'notes', 'status', 'created_at'];
    exportToCSV(cprBookings, 'cpr_bookings', headers);
  };

  const exportNotaryBookings = () => {
    const headers = ['name', 'email', 'phone', 'address', 'numberOfSigners', 'numberOfDocuments', 'preferredDate', 'preferredTime', 'documentType', 'notes', 'status', 'created_at'];
    exportToCSV(notaryBookings, 'notary_bookings', headers);
  };

  const exportContacts = () => {
    const headers = ['name', 'email', 'phone', 'subject', 'message', 'status', 'created_at'];
    exportToCSV(contacts, 'contacts', headers);
  };

  const exportAll = () => {
    if (cprBookings.length > 0) exportCPRBookings();
    if (notaryBookings.length > 0) exportNotaryBookings();
    if (contacts.length > 0) exportContacts();
    if (cprBookings.length === 0 && notaryBookings.length === 0 && contacts.length === 0) {
      toast.error('No data to export');
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const [cprRes, notaryRes, contactRes] = await Promise.all([
        axios.get(`${API}/bookings/cpr`),
        axios.get(`${API}/bookings/notary`),
        axios.get(`${API}/contacts`)
      ]);
      setCprBookings(cprRes.data.reverse());
      setNotaryBookings(notaryRes.data.reverse());
      setContacts(contactRes.data.reverse());
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
      // Auto refresh every 30 seconds
      const interval = setInterval(fetchBookings, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="shadow-2xl border-0">
            <CardHeader className="bg-gradient-to-r from-[#2a2873] to-[#5a2b8a] text-white rounded-t-lg">
              <div className="flex items-center justify-center gap-3">
                <Lock className="w-8 h-8" />
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Access</CardTitle>
              </div>
              <p className="text-white/70 text-center text-sm mt-2">Enter password to view bookings</p>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#2a2873] font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter admin password"
                      className="pr-10"
                      data-testid="admin-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {loginError && (
                  <p className="text-red-500 text-sm text-center">{loginError}</p>
                )}
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] rounded-full"
                  data-testid="admin-login-btn"
                >
                  <Lock className="w-4 h-4 mr-2" /> Sign In
                </Button>
              </form>
              <p className="text-center text-xs text-slate-400 mt-6">
                LifeBreath CPR & First Aid Admin Portal
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2a2873] to-[#5a2b8a] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Booking Journal</h1>
              <p className="text-white/70 mt-1">View all your CPR & Notary bookings</p>
            </div>
            <div className="flex items-center gap-4">
              <p className="text-sm text-white/60">Last updated: {formatTime(lastRefresh)}</p>
              <Button onClick={fetchBookings} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white" disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={handleLogout} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white">
                <Lock className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{cprBookings.length}</p>
              <p className="text-white/70 text-sm">CPR Bookings</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{notaryBookings.length}</p>
              <p className="text-white/70 text-sm">Notary Bookings</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{contacts.length}</p>
              <p className="text-white/70 text-sm">Contact Messages</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="cpr" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="cpr" className="data-[state=active]:bg-[#c31c31] data-[state=active]:text-white">
              <Heart className="w-4 h-4 mr-2" /> CPR Bookings ({cprBookings.length})
            </TabsTrigger>
            <TabsTrigger value="notary" className="data-[state=active]:bg-[#4f62c8] data-[state=active]:text-white">
              <FileCheck className="w-4 h-4 mr-2" /> Notary ({notaryBookings.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-[#5a2b8a] data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" /> Contacts ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* CPR Bookings */}
          <TabsContent value="cpr">
            <div className="space-y-4">
              {cprBookings.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Heart className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No CPR bookings yet</p>
                  </CardContent>
                </Card>
              ) : (
                cprBookings.map((booking, idx) => (
                  <motion.div key={booking.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                    <Card className="border-l-4 border-l-[#c31c31] hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start flex-wrap gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[#c31c31]">CPR/First Aid</Badge>
                              <Badge variant="outline">{booking.status || 'pending'}</Badge>
                              {booking.courseType?.includes('PROMO') && <Badge className="bg-green-500">$15 OFF</Badge>}
                            </div>
                            <h3 className="text-xl font-bold text-[#2a2873]">{booking.name}</h3>
                            <p className="text-[#5a2b8a] font-medium">{booking.courseType}</p>
                          </div>
                          <div className="text-right text-sm text-slate-500">
                            <p>Booked: {formatDate(booking.created_at)}</p>
                            <p>{formatTime(booking.created_at)}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-[#c31c31]" />
                            <span>{booking.preferredDate || 'TBD'} {booking.preferredTime && `@ ${booking.preferredTime}`}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-[#c31c31]" />
                            <a href={`tel:${booking.phone}`} className="text-[#c31c31] hover:underline">{booking.phone}</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-[#c31c31]" />
                            <a href={`mailto:${booking.email}`} className="text-[#c31c31] hover:underline">{booking.email}</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-4 h-4 text-[#c31c31]" />
                            <span>{booking.numberOfStudents} student(s)</span>
                          </div>
                        </div>
                        {(booking.address || booking.locationType) && (
                          <div className="flex items-center gap-2 text-sm mt-2">
                            <MapPin className="w-4 h-4 text-[#c31c31]" />
                            <span>{booking.locationType} {booking.address && `- ${booking.address}`}</span>
                          </div>
                        )}
                        {booking.notes && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                            <strong>Notes:</strong> {booking.notes}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Notary Bookings */}
          <TabsContent value="notary">
            <div className="space-y-4">
              {notaryBookings.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <FileCheck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No notary bookings yet</p>
                  </CardContent>
                </Card>
              ) : (
                notaryBookings.map((booking, idx) => (
                  <motion.div key={booking.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                    <Card className="border-l-4 border-l-[#4f62c8] hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start flex-wrap gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[#4f62c8]">Mobile Notary</Badge>
                              <Badge variant="outline">{booking.status || 'pending'}</Badge>
                            </div>
                            <h3 className="text-xl font-bold text-[#2a2873]">{booking.name}</h3>
                            {booking.documentType && <p className="text-[#5a2b8a]">{booking.documentType}</p>}
                          </div>
                          <div className="text-right text-sm text-slate-500">
                            <p>Booked: {formatDate(booking.created_at)}</p>
                            <p>{formatTime(booking.created_at)}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-[#4f62c8]" />
                            <span>{booking.preferredDate || 'TBD'} {booking.preferredTime && `@ ${booking.preferredTime}`}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-[#4f62c8]" />
                            <a href={`tel:${booking.phone}`} className="text-[#4f62c8] hover:underline">{booking.phone}</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-[#4f62c8]" />
                            <a href={`mailto:${booking.email}`} className="text-[#4f62c8] hover:underline">{booking.email}</a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <FileText className="w-4 h-4 text-[#4f62c8]" />
                            <span>{booking.numberOfSigners} signer(s), {booking.numberOfDocuments} doc(s)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm mt-2">
                          <MapPin className="w-4 h-4 text-[#4f62c8]" />
                          <span>{booking.address}</span>
                        </div>
                        {booking.notes && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                            <strong>Notes:</strong> {booking.notes}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Contact Messages */}
          <TabsContent value="contacts">
            <div className="space-y-4">
              {contacts.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No contact messages yet</p>
                  </CardContent>
                </Card>
              ) : (
                contacts.map((contact, idx) => (
                  <motion.div key={contact.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                    <Card className="border-l-4 border-l-[#5a2b8a] hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start flex-wrap gap-4">
                          <div>
                            <Badge className="bg-[#5a2b8a] mb-2">Contact</Badge>
                            <h3 className="text-xl font-bold text-[#2a2873]">{contact.name}</h3>
                            <p className="text-[#5a2b8a] font-medium">{contact.subject}</p>
                          </div>
                          <div className="text-right text-sm text-slate-500">
                            <p>{formatDate(contact.created_at)}</p>
                            <p>{formatTime(contact.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex gap-4 mt-4 pt-4 border-t flex-wrap">
                          <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-[#5a2b8a] hover:underline">
                            <Mail className="w-4 h-4" /> {contact.email}
                          </a>
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-[#5a2b8a] hover:underline">
                              <Phone className="w-4 h-4" /> {contact.phone}
                            </a>
                          )}
                        </div>
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                          {contact.message}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
